import { useEffect, useState } from 'react';
import apiClient from '../api/client';

/**
 * An image served by the API, as an object URL for an <img src>.
 *
 * An <img> pointed straight at an API route sends no Authorization or
 * X-Connection-Id header, so the API refuses it (and a resident photo must
 * not be readable without a login anyway). This fetches the image through
 * apiClient instead. Returns null while loading, when `path` is null, or when
 * there is no image; the object URL is released when the path changes or the
 * component unmounts.
 */
export function useApiImage(path: string | null): string | null {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setObjectUrl(null);
      return;
    }

    let cancelled = false;
    let created: string | null = null;

    apiClient
      .get<Blob>(path, { responseType: 'blob' })
      .then(({ data }) => {
        if (cancelled || !data || data.size === 0) return;
        created = URL.createObjectURL(data);
        setObjectUrl(created);
      })
      .catch(() => {
        if (!cancelled) setObjectUrl(null);
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
      setObjectUrl(null);
    };
  }, [path]);

  return objectUrl;
}
